import React, { useCallback, useState } from 'react';

import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';

const L = logger('ConsultationForm'); // eslint-disable-line no-unused-vars

const ConsultationForm = ({ client, onSave }) => {
  const form = client?.consultation_form;

  const [localValue, setLocalValue] = useState(fromAnyToString(form));
  const { val: isEdit, yay: startEdit, nay: stopEdit } = useTip(false);
  const { val: isDirty, yay: markIsDirty, nay: clearIsDirty } = useTip(false);
  const { t } = useTranslation();

  const hasHttp = /^(https?:\/\/)/.test(localValue);
  const hasDomain = localValue.split('.').length >= 2;
  const hasSpace = localValue.trim().indexOf(' ') >= 0;

  const isFull = fromAnyToBool(localValue);
  const isValid = !hasSpace && hasDomain && isFull;

  const isSave = isEdit && isValid;

  const url = hasHttp ? localValue : `https://${localValue}`;
  const href = isValid ? url : void 1;

  const handleSave = useCallback(() => {
    L.debug('handleSave()', isValid, url, localValue);
    if (!isValid) return;

    const { email, id, salon_id, phone } = client ?? {};

    call('ConsultationForm.onSave', onSave, { id, email, phone, salon_id, consultation_form: url });

    stopEdit();
    clearIsDirty();
  }, [isValid, url, localValue, client, onSave, stopEdit, clearIsDirty]);

  const handleEdit = useCallback(() => {
    L.debug('handleSave()', isValid, url, localValue);
    startEdit();
  }, [url, isValid, localValue, startEdit]);

  const handleChange = useCallback(
    (ev) => {
      setLocalValue(fromEventToValue(ev));
      markIsDirty();
    },
    [markIsDirty],
  );

  return (
    <div className="consultation-form">
      <label htmlFor="linkInput" className="font-16">
        {t('composite.clientProfile.consultationForm.title')}
      </label>

      <form className="form-link">
        {isEdit ? (
          <input
            className="input-default"
            id="linkInput"
            type="text"
            autoComplete="off"
            value={localValue}
            onChange={handleChange}
            placeholder={t('composite.clientProfile.consultationForm.linkPlaceholder')}
          />
        ) : (
          <a rel="noopener noreferrer" href={href} target="_blank">
            {' '}
            {localValue}
          </a>
        )}

        {isFull && isDirty && !isValid && (
          <span className="error">
            {t('composite.clientProfile.consultationForm.invalidUrl')}
            <ul>
              {!hasHttp && <li>{t('composite.clientProfile.consultationForm.noHttp')}</li>}
              {!hasDomain && <li>{t('composite.clientProfile.consultationForm.noDomain')}</li>}
              {hasSpace && <li>{t('composite.clientProfile.consultationForm.spaces')}</li>}
            </ul>
          </span>
        )}

        {isSave ? (
          <button className="btn btn-violet-link" onClick={handleSave}>
            {t('composite.clientProfile.consultationForm.saveButton')}
          </button>
        ) : (
          <button className="btn btn-violet-link" onClick={handleEdit}>
            {t('composite.clientProfile.consultationForm.editButton')}
          </button>
        )}
      </form>
    </div>
  );
};

export default ConsultationForm;
